import React, { useState, useEffect } from 'react';
import { 
  Calendar, 
  ChefHat, 
  Plus, 
  Search, 
  BookOpen, 
  Star, 
  Clock,
  Users,
  ShoppingCart,
  Filter,
  Heart,
  Target,
  TrendingUp,
  X,
  Edit,
  Trash2
} from 'lucide-react';
import { barcodeApi } from '../../lib/barcodeApi';
import { localUsdaApi } from '../../lib/localUsdaApi';
import { useExampleData } from '../../data/example/ExampleDataProvider';
import { MockBadge } from '../common/MockBadge';
import { DataDisclaimer } from '../common/DataDisclaimer';
import { getComponentClass } from '../../styles/theme';

interface Recipe {
  id: string;
  name: string;
  description: string;
  servings: number;
  prep_time: number;
  cook_time: number;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  cuisine_type: string;
  dietary_tags: string[];
  ingredients: RecipeIngredient[];
  instructions: string[];
  nutrition_per_serving: {
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
    fiber: number;
  };
  source: 'guru' | 'personal' | 'public_domain' | 'community';
  source_details: {
    guru_name?: string;
    book_title?: string;
    contributed_by?: string;
    scan_source?: string;
  };
  image_url?: string;
  rating: number;
  save_count: number;
  created_at: string;
  updated_at: string;
}

interface RecipeIngredient {
  id: string;
  name: string;
  amount: number;
  unit: string;
  fdc_id?: number; // Link to USDA database
  ndc_code?: string; // For supplements/medications
  notes?: string;
  substitutions?: string[];
}

interface MealPlan {
  id: string;
  user_id: string;
  date: string;
  meals: {
    breakfast?: Recipe[];
    lunch?: Recipe[];
    dinner?: Recipe[];
    snacks?: Recipe[];
  };
  daily_nutrition: {
    calories: number;
    protein: number;
    carbs: number;
    fat: number;
    fiber: number;
  };
  grocery_list_generated: boolean;
  notes?: string;
}

interface DietPlan {
  id: string;
  name: string;
  description: string;
  type: 'keto' | 'mediterranean' | 'paleo' | 'vegan' | 'low_carb' | 'diabetic' | 'custom';
  daily_targets: {
    calories: number;
    protein_percent: number;
    carbs_percent: number;
    fat_percent: number;
  };
  restrictions: string[];
  recommended_recipes: string[];
  guru_id?: string;
  duration_weeks?: number;
}

const MealPlanner: React.FC = () => {
  const [currentWeek, setCurrentWeek] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [mealPlans, setMealPlans] = useState<MealPlan[]>([]);
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [dietPlans, setDietPlans] = useState<DietPlan[]>([]);
  const [activeDietPlan, setActiveDietPlan] = useState<DietPlan | null>(null);
  
  const [showRecipeSearch, setShowRecipeSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFilters, setSearchFilters] = useState({
    cuisine: '',
    dietary: '',
    difficulty: '',
    source: '',
    max_prep_time: 0
  });

  const [selectedMealType, setSelectedMealType] = useState<'breakfast' | 'lunch' | 'dinner' | 'snacks'>('breakfast');
  const [showGroceryList, setShowGroceryList] = useState(false);
  const [isAddMealOpen, setIsAddMealOpen] = useState(false);

  const { getMealPlans, getRecipes, getDietPlans, shouldShowExampleData } = useExampleData();
  const isExampleData = shouldShowExampleData('meal-planning');

  useEffect(() => {
    loadMealPlans();
    loadRecipes();
    loadDietPlans();
  }, []);

  const loadMealPlans = async () => {
    if (isExampleData) {
      const exampleMealPlans = getMealPlans();
      setMealPlans(exampleMealPlans as MealPlan[]);
    } else {
      // Load from real API
      // const apiMealPlans = await fetchMealPlansFromAPI();
      // setMealPlans(apiMealPlans);
      setMealPlans([]);
    }
  };

  const loadRecipes = async () => {
    if (isExampleData) {
      const exampleRecipes = getRecipes();
      setRecipes(exampleRecipes as Recipe[]);
    } else {
      // Load from real API
      // const apiRecipes = await fetchRecipesFromAPI();
      // setRecipes(apiRecipes);
      setRecipes([]);
    }
  };

  const loadDietPlans = async () => {
    if (isExampleData) {
      const exampleDietPlans = getDietPlans();
      setDietPlans(exampleDietPlans as DietPlan[]);
    } else {
      // Load from real API
      // const apiDietPlans = await fetchDietPlansFromAPI();
      // setDietPlans(apiDietPlans);
      setDietPlans([]);
    }
  };

  const addRecipeToMeal = (recipe: Recipe, date: string, mealType: keyof MealPlan['meals']) => {
    setMealPlans(prev => prev.map(plan => {
      if (plan.date === date) {
        const updatedMeals = { ...plan.meals };
        updatedMeals[mealType] = [...updatedMeals[mealType], recipe];
        
        // Recalculate daily nutrition
        const dailyNutrition = calculateDailyNutrition(updatedMeals);
        
        return {
          ...plan,
          meals: updatedMeals,
          daily_nutrition: dailyNutrition
        };
      }
      return plan;
    }));
    setShowRecipeSearch(false);
  };

  const calculateDailyNutrition = (meals: MealPlan['meals']) => {
    let totalNutrition = { calories: 0, protein: 0, carbs: 0, fat: 0, fiber: 0 };
    
    Object.values(meals).forEach(mealRecipes => {
      mealRecipes.forEach(recipe => {
        totalNutrition.calories += recipe.nutrition_per_serving.calories;
        totalNutrition.protein += recipe.nutrition_per_serving.protein;
        totalNutrition.carbs += recipe.nutrition_per_serving.carbs;
        totalNutrition.fat += recipe.nutrition_per_serving.fat;
        totalNutrition.fiber += recipe.nutrition_per_serving.fiber;
      });
    });
    
    return totalNutrition;
  };

  const generateGroceryList = async (weekPlans: MealPlan[]) => {
    const groceryItems = new Map<string, {
      name: string;
      total_amount: number;
      unit: string;
      fdc_id?: number;
      estimated_cost?: number;
      aisle?: string;
    }>();

    weekPlans.forEach(plan => {
      Object.values(plan.meals).forEach(mealRecipes => {
        mealRecipes?.forEach(recipe => {
          recipe.ingredients.forEach(ingredient => {
            const key = ingredient.name.toLowerCase();
            if (groceryItems.has(key)) {
              const existing = groceryItems.get(key)!;
              existing.total_amount += ingredient.amount;
            } else {
              groceryItems.set(key, {
                name: ingredient.name,
                total_amount: ingredient.amount,
                unit: ingredient.unit,
                fdc_id: ingredient.fdc_id,
                aisle: categorizeIngredient(ingredient.name)
              });
            }
          });
        });
      });
    });

    return Array.from(groceryItems.values());
  };

  const categorizeIngredient = (ingredientName: string): string => {
    const categories = {
      'Produce': ['tomato', 'lettuce', 'onion', 'garlic', 'apple', 'banana'],
      'Dairy': ['milk', 'cheese', 'yogurt', 'butter'],
      'Meat': ['chicken', 'beef', 'pork', 'fish'],
      'Pantry': ['quinoa', 'rice', 'pasta', 'oil', 'spices'],
      'Frozen': ['frozen'],
      'Bakery': ['bread', 'bagel']
    };

    for (const [category, keywords] of Object.entries(categories)) {
      if (keywords.some(keyword => ingredientName.toLowerCase().includes(keyword))) {
        return category;
      }
    }
    return 'Other';
  };

  const getWeekDays = () => {
    const week = [];
    const startOfWeek = new Date(currentWeek);
    startOfWeek.setDate(currentWeek.getDate() - currentWeek.getDay());
    
    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      week.push(day);
    }
    return week;
  };

  const getMealPlanForDate = (date: Date): MealPlan | undefined => {
    const dateString = date.toISOString().split('T')[0];
    return mealPlans.find(plan => plan.date === dateString);
  };

  const filteredRecipes = recipes.filter(recipe => {
    if (searchQuery && !recipe.name.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    if (searchFilters.cuisine && recipe.cuisine_type !== searchFilters.cuisine) {
      return false;
    }
    if (searchFilters.dietary && !recipe.dietary_tags.includes(searchFilters.dietary)) {
      return false;
    }
    if (searchFilters.difficulty && recipe.difficulty !== searchFilters.difficulty) {
      return false;
    }
    if (searchFilters.source && recipe.source !== searchFilters.source) {
      return false;
    }
    if (searchFilters.max_prep_time && recipe.prep_time > searchFilters.max_prep_time) {
      return false;
    }
    return true;
  });

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6">
      {/* Header and Toolbar Reconstruction */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            <Calendar className="w-8 h-8 mr-3 text-blue-600" />
            Meal Planner
          </h1>
          <p className="text-gray-600 mt-1">
            Plan your meals with guru recommendations and personal recipes
          </p>
        </div>
        <div className="flex space-x-3 mt-4 md:mt-0">
          <button className="bg-green-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-green-700">
            <Plus className="w-4 h-4" />
            Add Recipe
          </button>
          <button className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2 hover:bg-blue-700">
            <ShoppingCart className="w-4 h-4" />
            Grocery List
          </button>
        </div>
      </div>

      {/* Example Data Disclaimer */}
      {isExampleData && (
        <DataDisclaimer 
          type="info"
          title="Example Meal Plans"
          message="These are example meal plans and recipes for demonstration. Connect with real nutritionists and chefs when you're ready to plan your meals."
          className="mb-6"
        />
      )}
      <div />

      {/* Week Navigation */}
      <div className="flex items-center justify-between bg-white dark:bg-slate-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <button
          onClick={() => {
            const prevWeek = new Date(currentWeek);
            prevWeek.setDate(currentWeek.getDate() - 7);
            setCurrentWeek(prevWeek);
          }}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          ←
        </button>
        
        <div className="flex space-x-2">
          {getWeekDays().map((day, index) => {
            const isToday = day.toDateString() === new Date().toDateString();
            const isSelected = day.toDateString() === selectedDate.toDateString();
            const mealPlan = getMealPlanForDate(day);
            const hasPlans = mealPlan && Object.values(mealPlan.meals).some(meals => meals.length > 0);
            
            return (
              <button
                key={index}
                onClick={() => setSelectedDate(day)}
                className={`p-3 rounded-lg text-center min-w-[100px] ${
                  isSelected 
                    ? 'bg-blue-600 text-white' 
                    : isToday 
                    ? 'bg-blue-50 text-blue-600 border border-blue-200'
                    : 'hover:bg-gray-50'
                }`}
              >
                <div className="text-sm font-medium">
                  {day.toLocaleDateString('en-US', { weekday: 'short' })}
                </div>
                <div className="text-lg font-bold">
                  {day.getDate()}
                </div>
                {hasPlans && (
                  <div className="w-2 h-2 bg-green-500 rounded-full mx-auto mt-1"></div>
                )}
              </button>
            );
          })}
        </div>
        
        <button
          onClick={() => {
            const nextWeek = new Date(currentWeek);
            nextWeek.setDate(currentWeek.getDate() + 7);
            setCurrentWeek(nextWeek);
          }}
          className="p-2 hover:bg-gray-100 rounded-lg"
        >
          →
        </button>
      </div>

      {/* Daily Meal Plan */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {(['breakfast', 'lunch', 'dinner', 'snacks'] as const).map((mealType) => {
          const mealPlan = getMealPlanForDate(selectedDate);
          const mealRecipes = mealPlan?.meals[mealType] || [];
          
          return (
            <div key={mealType} className="bg-white dark:bg-slate-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-slate-900 dark:text-white capitalize flex items-center">
                  <ChefHat className="w-4 h-4 mr-2 text-gray-600" />
                  {mealType}
                </h3>
                <button
                  onClick={() => {
                    setSelectedMealType(mealType);
                    setShowRecipeSearch(true);
                  }}
                  className="text-blue-600 hover:text-blue-800"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
              
              <div className="space-y-3">
                {mealRecipes.length === 0 ? (
                  <div className="text-center py-6 text-gray-500">
                    <ChefHat className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                    <p className="text-sm">No {mealType} planned</p>
                    <button
                      onClick={() => {
                        setSelectedMealType(mealType);
                        setShowRecipeSearch(true);
                      }}
                      className="text-blue-600 hover:text-blue-800 text-sm mt-1"
                    >
                      Add recipe
                    </button>
                  </div>
                ) : (
                  mealRecipes.map((recipe, index) => (
                    <div key={index} className="border border-gray-100 rounded-lg p-3 hover:bg-gray-50">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900 text-sm">{recipe.name}</h4>
                          <div className="flex items-center space-x-2 mt-1 text-xs text-gray-600">
                            <Clock className="w-3 h-3" />
                            <span>{recipe.prep_time + recipe.cook_time}m</span>
                            <Users className="w-3 h-3" />
                            <span>{recipe.servings}</span>
                            <Star className="w-3 h-3 text-yellow-500" />
                            <span>{recipe.rating}</span>
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            {recipe.nutrition_per_serving.calories} cal • 
                            {recipe.nutrition_per_serving.protein}g protein
                          </div>
                        </div>
                        <button className="text-gray-400 hover:text-red-600">
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Daily Nutrition Summary */}
      {getMealPlanForDate(selectedDate) && (
        <div className={`${getComponentClass('card')} p-6`}>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-white mb-4">Nutritional Summary</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="text-center p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
              <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{Math.round(getMealPlanForDate(selectedDate)!.daily_nutrition.calories)}</p>
              <p className="text-sm text-slate-600 dark:text-slate-400">Calories</p>
            </div>
            <div className="text-center p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">{Math.round(getMealPlanForDate(selectedDate)!.daily_nutrition.protein)}g</p>
              <p className="text-sm text-slate-600 dark:text-slate-400">Protein</p>
            </div>
            <div className="text-center p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg">
              <p className="text-2xl font-bold text-yellow-600 dark:text-yellow-400">{Math.round(getMealPlanForDate(selectedDate)!.daily_nutrition.carbs)}g</p>
              <p className="text-sm text-slate-600 dark:text-slate-400">Carbs</p>
            </div>
            <div className="text-center p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
              <p className="text-2xl font-bold text-red-600 dark:text-red-400">{Math.round(getMealPlanForDate(selectedDate)!.daily_nutrition.fat)}g</p>
              <p className="text-sm text-slate-600 dark:text-slate-400">Fat</p>
            </div>
          </div>
        </div>
      )}

      {/* Recipe Search Modal */}
      {showRecipeSearch && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                <Search className="w-5 h-5 mr-2 text-blue-600" />
                Add Recipe to {selectedMealType}
              </h3>
              <button
                onClick={() => setShowRecipeSearch(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            {/* Search and Filters */}
            <div className="p-6 border-b space-y-4">
              <div className="flex space-x-4">
                <div className="flex-1">
                  <input
                    type="text"
                    placeholder="Search recipes..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <button className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center">
                  <Filter className="w-4 h-4 mr-2" />
                  Filters
                </button>
              </div>
              
              <div className="flex space-x-4 text-sm">
                <select
                  value={searchFilters.source}
                  onChange={(e) => setSearchFilters(prev => ({ ...prev, source: e.target.value }))}
                  className="px-3 py-1 border border-gray-300 rounded"
                >
                  <option value="">All Sources</option>
                  <option value="guru">Guru Recipes</option>
                  <option value="personal">My Recipes</option>
                  <option value="public_domain">Public Domain</option>
                  <option value="community">Community</option>
                </select>
                
                <select
                  value={searchFilters.difficulty}
                  onChange={(e) => setSearchFilters(prev => ({ ...prev, difficulty: e.target.value }))}
                  className="px-3 py-1 border border-gray-300 rounded"
                >
                  <option value="">Any Difficulty</option>
                  <option value="Easy">Easy</option>
                  <option value="Medium">Medium</option>
                  <option value="Hard">Hard</option>
                </select>
                
                <select
                  value={searchFilters.dietary}
                  onChange={(e) => setSearchFilters(prev => ({ ...prev, dietary: e.target.value }))}
                  className="px-3 py-1 border border-gray-300 rounded"
                >
                  <option value="">All Diets</option>
                  <option value="vegetarian">Vegetarian</option>
                  <option value="vegan">Vegan</option>
                  <option value="gluten-free">Gluten-Free</option>
                  <option value="keto">Keto</option>
                  <option value="low-carb">Low Carb</option>
                </select>
              </div>
            </div>
            
            {/* Recipe Results */}
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {filteredRecipes.map((recipe) => (
                  <div key={recipe.id} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:border-blue-300 dark:hover:border-blue-600">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-semibold text-slate-900 dark:text-white">{recipe.name}</h4>
                      <div className="flex items-center space-x-1">
                        <Star className="h-4 w-4 text-yellow-500 fill-current" />
                        <span className="text-sm text-slate-600 dark:text-slate-400">{recipe.rating}</span>
                      </div>
                    </div>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">{recipe.description}</p>
                    <div className="flex items-center justify-between text-xs text-slate-500 dark:text-slate-400">
                      <span>{recipe.prep_time + recipe.cook_time} min</span>
                      <span>{recipe.servings} servings</span>
                    </div>
                    
                    <div className="flex space-x-2 mt-2">
                      {recipe.dietary_tags.slice(0, 2).map((tag) => (
                        <span key={tag} className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              
              {filteredRecipes.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  <BookOpen className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>No recipes found matching your criteria</p>
                  <button className="text-blue-600 hover:text-blue-800 mt-2">
                    Browse all recipes
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MealPlanner; 
